import { useState, useCallback, useEffect, useRef } from 'react';
import { CSVFileEntry, DatenformOption, DatenanpassungOption } from '../../../types/common';
import { sessionsApi } from '@/api';
import { getApiUrl } from '../../../config/environment';
import { getAuthToken } from '../../../core/auth/supabase-auth';

// ============================================
// TYPES FOR TRAINING STATE RESTORATION
// ============================================

export interface TrainingStatusResponse {
    status: 'in_progress' | 'completed' | 'not_found' | 'failed';
    progress: number;
    current_step: string;
    total_steps: number;
    completed_steps: number;
    started_at?: string;
    completed_at?: string;
    message?: string;
}

/**
 * [WORKFLOW_DEBUG] Training results info for session restoration
 */
export interface TrainingResultsInfo {
    hasViolinPlots: boolean;
    violinPlots: Record<string, unknown> | null;
    hasTrainedModel: boolean;
    datasetCount: number;
    workflowPhase: string;  // [WORKFLOW_DEBUG] Added for session restoration
}

export interface SessionTrainingState {
    sessionName?: string;
    timeInfo: {
        session_id: string;
        jahr: boolean;
        woche: boolean;
        monat: boolean;
        feiertag: boolean;
        tag: boolean;
        zeitzone: string;
        category_data: Record<string, unknown>;
    };
    zeitschritte: {
        eingabe: string;
        ausgabe: string;
        zeitschrittweite: string;
        offset: string;
    };
    inputFiles: CSVFileEntry[];
    outputFiles: CSVFileEntry[];
    // Legacy field for backwards compatibility
    analysisCompleted: boolean;
    // NEW: Training state restoration data
    trainingStatus: TrainingStatusResponse;
    resultsInfo: TrainingResultsInfo;
}

export const useSessionManagement = () => {
    const [uploadSessionId, setUploadSessionId] = useState<string | null>(null);
    const [databaseFilesCount, setDatabaseFilesCount] = useState<number | null>(null);
    const [isLoadingFilesCount, setIsLoadingFilesCount] = useState<boolean>(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const fetchDatabaseFilesCount = useCallback(async () => {
        if (!uploadSessionId || !mountedRef.current) return;

        try {
            setIsLoadingFilesCount(true);
            const token = await getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            // Call backend database session endpoint to get n_dat from database
            const apiUrl = getApiUrl(`/api/training/session/${uploadSessionId}/database`);
            const response = await fetch(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            const data = await response.json();


            if (mountedRef.current) {
                if (data.success && data.session) {
                    // Use n_dat (total data points from CSV files)
                    const datasetCount = data.session.n_dat || 0;
                    setDatabaseFilesCount(datasetCount);
                } else {
                    setDatabaseFilesCount(null);
                }
            }
        } catch {
            if (mountedRef.current) {
                setDatabaseFilesCount(null);
            }
        } finally {
            if (mountedRef.current) {
                setIsLoadingFilesCount(false);
            }
        }
    }, [uploadSessionId]);

    const loadExistingSessionData = useCallback(async (sessionId: string): Promise<SessionTrainingState | null> => {
        if (!mountedRef.current) return null;

        try {
            const token = await getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            // 1. Load basic session data (existing logic)
            const apiUrl = getApiUrl(`/api/training/session/${sessionId}/database`);

            const response = await fetch(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            const sessionDetails = await response.json();

            if (!response.ok || !sessionDetails.success) {
                throw new Error('Failed to load session data from backend');
            }

            // Extract individual components from backend response
            const sessionData = sessionDetails.session;
            const timeInfoData = sessionData.timeInfo;
            const zeitschritteData = sessionData.zeitschritte;
            const filesData = sessionData.files || [];

            // Update databaseFilesCount with n_dat from session if available
            if (mountedRef.current && sessionData.n_dat !== undefined && sessionData.n_dat !== null) {
                setDatabaseFilesCount(sessionData.n_dat);
            }

            if (!mountedRef.current) return null;

            const convertFiles = (files: unknown[], type: 'input' | 'output'): CSVFileEntry[] => {
                return files.map(file => {
                    const f = file as {
                        id: string;
                        fileName: string;
                        bezeichnung?: string;
                        utcMin?: string;
                        utcMax?: string;
                        zeitschrittweite?: string;
                        min?: string;
                        max?: string;
                        offset?: string;
                        datenpunkte?: string;
                        numerischeDatenpunkte?: string;
                        numerischerAnteil?: string;
                        datenform?: string;
                        zeithorizontStart?: string;
                        zeithorizontEnd?: string;
                        zeitschrittweiteTransferiertenDaten?: string;
                        offsetTransferiertenDaten?: string;
                        mittelwertbildungÜberDenZeithorizont?: string;
                        datenanpassung?: string;
                        zeitschrittweiteMinValue?: string;
                        zeitschrittweiteAvgValue?: string;
                        skalierung?: string;
                        skalierungMax?: string;
                        skalierungMin?: string;
                        storagePath?: string;
                        createdAt?: string;
                        updatedAt?: string;
                    };
                    return {
                        id: f.id,
                        file: null,
                        fileName: f.fileName,
                        bezeichnung: f.bezeichnung || '',
                        utcMin: f.utcMin || '',
                        utcMax: f.utcMax || '',
                        zeitschrittweite: f.zeitschrittweite || '',
                        min: f.min || '',
                        max: f.max || '',
                        offset: f.offset || '',
                        datenpunkte: f.datenpunkte || '',
                        numerischeDatenpunkte: f.numerischeDatenpunkte || '',
                        numerischerAnteil: f.numerischerAnteil || '',
                        datenform: (f.datenform || '') as DatenformOption,
                        zeithorizontStart: f.zeithorizontStart || '',
                        zeithorizontEnd: f.zeithorizontEnd || '',
                        zeitschrittweiteTransferiertenDaten: f.zeitschrittweiteTransferiertenDaten || '',
                        offsetTransferiertenDaten: f.offsetTransferiertenDaten || '',
                        mittelwertbildungÜberDenZeithorizont: (f.mittelwertbildungÜberDenZeithorizont as 'ja' | 'nein') || 'nein',
                        datenanpassung: (f.datenanpassung || '') as DatenanpassungOption,
                        zeitschrittweiteMinValue: f.zeitschrittweiteMinValue || '',
                        zeitschrittweiteAvgValue: f.zeitschrittweiteAvgValue || '',
                        skalierung: (f.skalierung as 'ja' | 'nein') || 'ja',
                        skalierungMax: f.skalierungMax || '1',
                        skalierungMin: f.skalierungMin || '0',
                        storagePath: f.storagePath || '',
                        type,
                        createdAt: f.createdAt || '',
                        updatedAt: f.updatedAt || ''
                    };
                });
            };

            const inputFiles = filesData ? convertFiles(filesData.filter((f: unknown) => (f as {type: string}).type === 'input'), 'input') : [];
            const outputFiles = filesData ? convertFiles(filesData.filter((f: unknown) => (f as {type: string}).type === 'output'), 'output') : [];

            // 2. Check training status and results in parallel
            const [trainingStatus, resultsInfo] = await Promise.all([
                checkTrainingStatus(sessionId),
                checkExistingResults(sessionId)
            ]);

            if (!mountedRef.current) {
                return null;
            }

            // Determine analysisCompleted for backwards compatibility
            const analysisCompleted = resultsInfo.hasTrainedModel ||
                                      trainingStatus.status === 'completed';

            const result: SessionTrainingState = {
                sessionName: sessionData.sessionName || undefined,
                timeInfo: timeInfoData || {
                    session_id: sessionId,
                    jahr: false,
                    woche: false,
                    monat: false,
                    feiertag: false,
                    tag: false,
                    zeitzone: '',
                    category_data: {}
                },
                zeitschritte: zeitschritteData || {
                    eingabe: '',
                    ausgabe: '',
                    zeitschrittweite: '',
                    offset: ''
                },
                inputFiles,
                outputFiles,
                analysisCompleted,
                trainingStatus,
                resultsInfo
            };

            return result;
        } catch {
            return null;
        }
    }, []);

    // ============================================
    // TRAINING STATE CHECK FUNCTIONS
    // ============================================

    /**
     * Check current training status from backend
     * Returns detailed status including progress and current step
     */
    const checkTrainingStatus = async (sessionId: string): Promise<TrainingStatusResponse> => {
        const defaultStatus: TrainingStatusResponse = {
            status: 'not_found',
            progress: 0,
            current_step: '',
            total_steps: 7,
            completed_steps: 0,
            message: 'No training found'
        };

        try {
            const token = await getAuthToken();
            if (!token) {
                return defaultStatus;
            }

            const apiUrl = getApiUrl(`/api/training/status/${sessionId}`);

            const response = await fetch(apiUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                return defaultStatus;
            }

            const data = await response.json();

            // Map backend status to our expected format
            let mappedStatus: TrainingStatusResponse['status'] = 'not_found';
            if (data.status === 'running' || data.status === 'in_progress') {
                mappedStatus = 'in_progress';
            } else if (data.status === 'completed' || data.status === 'training_completed') {
                mappedStatus = 'completed';
            } else if (data.status === 'failed' || data.status === 'error') {
                mappedStatus = 'failed';
            }

            const result: TrainingStatusResponse = {
                status: mappedStatus,
                progress: data.progress || data.overall_progress || 0,
                current_step: data.current_step || data.message || '',
                total_steps: data.total_steps || 7,
                completed_steps: data.completed_steps || 0,
                started_at: data.started_at,
                completed_at: data.completed_at,
                message: data.message
            };

            return result;
        } catch {
            return defaultStatus;
        }
    };

    /**
     * Check for existing training results (violin plots, trained models)
     * Uses lightweight /results-summary endpoint instead of full /results
     * [WORKFLOW_DEBUG] Now also includes workflow_phase for session restoration
     */
    const checkExistingResults = async (sessionId: string): Promise<TrainingResultsInfo> => {
        const defaultInfo: TrainingResultsInfo = {
            hasViolinPlots: false,
            violinPlots: null,
            hasTrainedModel: false,
            datasetCount: 0,
            workflowPhase: 'upload'  // [WORKFLOW_DEBUG] Default phase
        };

        try {
            const token = await getAuthToken();
            if (!token) {
                console.log('[WORKFLOW_DEBUG] checkExistingResults: No auth token');
                return defaultInfo;
            }

            // Use lightweight summary endpoint instead of full results
            const apiUrl = getApiUrl(`/api/training/results-summary/${sessionId}`);

            const response = await fetch(apiUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                console.log('[WORKFLOW_DEBUG] checkExistingResults: Response not OK', response.status);
                return defaultInfo;
            }

            const data = await response.json();

            // New endpoint returns clean boolean flags directly
            const hasViolinPlots = data.has_violin_plots || false;
            const hasTrainedModel = data.has_trained_model || false;
            const datasetCount = data.n_dat || 0;
            // [WORKFLOW_DEBUG] Get workflow_phase from backend response
            const workflowPhase = data.workflow_phase || 'upload';

            console.log('[WORKFLOW_DEBUG] checkExistingResults:', {
                sessionId,
                hasViolinPlots,
                hasTrainedModel,
                datasetCount,
                workflowPhase
            });

            const result: TrainingResultsInfo = {
                hasViolinPlots,
                violinPlots: null,  // Violin plots loaded separately via /visualizations
                hasTrainedModel,
                datasetCount,
                workflowPhase  // [WORKFLOW_DEBUG] From backend
            };

            return result;
        } catch (error) {
            console.error('[WORKFLOW_DEBUG] checkExistingResults error:', error);
            return defaultInfo;
        }
    };

    const checkSessionExists = useCallback(async (sessionId: string): Promise<boolean> => {
        try {
            const token = await getAuthToken();
            if (!token) return false;

            const apiUrl = getApiUrl(`/api/training/session-status/${sessionId}`);
            const response = await fetch(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            return response.ok;
        } catch {
            return false;
        }
    }, []);

    const createDatabaseSession = useCallback(async (sessionId: string): Promise<string> => {
        try {
            const token = await getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const apiUrl = getApiUrl('/api/training/create-database-session');
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sessionId })
            });

            if (response.ok) {
                const { sessionUuid } = await response.json();
                return sessionUuid;
            }
            return sessionId;
        } catch {
            return sessionId;
        }
    }, []);

    const saveSessionToStorage = useCallback((sessionId: string) => {
        localStorage.setItem('lastSessionId', sessionId);
    }, []);

    const clearSessionFromStorage = useCallback(() => {
        localStorage.removeItem('lastSessionId');
    }, []);

    const getSessionFromStorage = useCallback(() => {
        return localStorage.getItem('lastSessionId');
    }, []);

    const resetAllData = useCallback(async () => {
        await sessionsApi.deleteAllSessions();
        clearSessionFromStorage();
        if (mountedRef.current) {
            setUploadSessionId(null);
            setDatabaseFilesCount(null);
        }
    }, [clearSessionFromStorage]);

    const resetDatabaseFilesCount = useCallback(() => {
        if (mountedRef.current) {
            setDatabaseFilesCount(null);
        }
    }, []);

    return {
        uploadSessionId,
        setUploadSessionId,
        databaseFilesCount,
        isLoadingFilesCount,
        fetchDatabaseFilesCount,
        loadExistingSessionData,
        checkSessionExists,
        createDatabaseSession,
        saveSessionToStorage,
        clearSessionFromStorage,
        getSessionFromStorage,
        resetAllData,
        resetDatabaseFilesCount
    };
};